import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import BusinessService from 'services/business.api';
import UserService from 'services/user.api';
import SubscriptionPlanService from 'services/subscriptionPlan.api';
import { Business, User, SubscriptionPlan } from 'services/@types';
import { normalizeSubscription } from 'utils/normalizeSubscription';

export default function useBusinessDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();

  const [business, setBusiness] = useState<Business | null>(null);
  const [linkedUsers, setLinkedUsers] = useState<User[]>([]);
  const [allPlans, setAllPlans] = useState<SubscriptionPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [businessData, usersData] = await Promise.all([
        BusinessService.getBusiness(id),
        UserService.getUsers({ businessID: id, limit: 100 }),
      ]);
      setBusiness(businessData);
      setLinkedUsers((usersData?.results as User[]) || []);
    } catch (error) {
      console.error('Failed to fetch business detail:', error);
      toast({
        title: t('toast.title.error'),
        description: 'Failed to load business details',
        status: 'error',
        duration: 3000,
      });
    }

    try {
      const plansData = await SubscriptionPlanService.getSubscriptionPlans({
        limit: 100,
      });
      setAllPlans(plansData?.results || []);
    } catch (error) {
      console.error('Failed to fetch subscription plans:', error);
      setAllPlans([]);
    }
    setLoading(false);
  }, [id, toast, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateSubscription = useCallback(
    async (updates: Record<string, any>) => {
      if (!id || !business) return;
      setSaving(true);
      try {
        const updated = await BusinessService.updateBusiness(id, {
          subscription: {
            ...normalizeSubscription(business.subscription || {}),
            ...updates,
          },
        } as any);
        setBusiness(updated);
        toast({
          title: t('toast.title.success'),
          description: t('toast.description.business_updated'),
          status: 'success',
          duration: 3000,
        });
      } catch (error) {
        toast({
          title: t('toast.title.error'),
          description: 'Failed to update subscription',
          status: 'error',
          duration: 3000,
        });
      }
      setSaving(false);
    },
    [id, business, toast, t],
  );

  const switchPlan = useCallback(
    async (planId: string) => {
      if (!id) return;
      setSaving(true);
      try {
        await BusinessService.switchSubscriptionPlan(id, planId);
        await fetchData();
        toast({
          title: t('toast.title.success'),
          description: 'Subscription plan switched successfully',
          status: 'success',
          duration: 3000,
        });
      } catch (error) {
        toast({
          title: t('toast.title.error'),
          description: 'Failed to switch subscription plan',
          status: 'error',
          duration: 3000,
        });
      }
      setSaving(false);
    },
    [id, fetchData, toast, t],
  );

  return {
    business,
    linkedUsers,
    allPlans,
    loading,
    saving,
    updateSubscription,
    switchPlan,
    navigate,
  };
}
